import React, {
  useState, useEffect, useCallback, ReactElement,
} from 'react';
import {
  Link,
} from 'react-router-dom';
import {
  Modal, Input, Loader, Segment, Button, Message, Image, List, MessageProps, ButtonProps,
} from 'semantic-ui-react';
import {
  useAppSelector
} from 'store';
import api from 'api';
import util from 'utils/utils';
import useThrottle from 'utils/useThrottle';

import IdeasImage from 'src/images/ideas.png';
import { ThemedButton, LargeCheckbox } from 'components/lib/UI';

type ChallengeChooserProps = {
  trigger?: ReactElement;
  ignores?: string[];
  createNewAction?: () => void;
  instructions?: string;
  onComplete: (challenge: any) => void;
  controlled?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  messageProps?: MessageProps;
  buttonProps?: ButtonProps;
}

const ChallengeChooser = ({
  trigger, ignores, createNewAction, instructions, onComplete, controlled, isOpen, onClose, messageProps, buttonProps = {},
} : ChallengeChooserProps) => {
  const user = useAppSelector((state) => state.user);
  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [challengeSearchState, setChallengesSearchState] = useState({
    page: 1,
    limit: 10,
    query: '',
  });
  const userId = user?._id;

  const getChallenges = useThrottle(() => {
    if (open && userId) {
      setLoading(true);
    }
    api.users.getChallenges(userId, challengeSearchState, (data) => {
      setLoading(false);
      let { challenges: newChallenges } = data;
      if (ignores?.length > 0) newChallenges = newChallenges.filter((c) => ignores.indexOf(c._id) === -1);
      setChallenges(newChallenges);
    }, () => { });
  }, 400, [open, userId, ignores, challengeSearchState], { useLeadingCall: true });

  useEffect(() => {
    getChallenges();
  }, [getChallenges]);

  const onClickChallenge = useCallback((challenge) => {
    if (selectedChallenge?._id === challenge._id) setSelectedChallenge(null);
    else setSelectedChallenge(challenge);
  }, [selectedChallenge]);

  const onChoose = useCallback((challenge) => {
    onComplete(challenge);
    setSelectedChallenge(null);
  }, [onComplete]);

  return (
    <>
      {controlled ? null : (
        <>
        {trigger
          ? React.cloneElement(trigger, { onClick: () => setOpen(!open) })
          : <Button size='small' primary icon='lightbulb' content='Choose a challenge' onClick={() => setOpen(!open)} />
          }
        </>
      )}

      <Modal open={controlled ? isOpen : open} size='tiny'
        onClose={() => {
          if (controlled) onClose();
          else setOpen(false);
        }}
      >
        <Modal.Header>
          <h3>Choose a challenge</h3>
          {instructions && <h5 style={{ marginTop: 0 }}>{instructions}</h5>}
        </Modal.Header>
        <Modal.Content>
          {messageProps && <Message {...messageProps} />}
          {loading && <Loader active />}
          {!loading && !challenges?.length
            && <Segment placeholder textAlign='center'>
              <img src={IdeasImage} style={{
                maxHeight: 100, maxWidth: '80%', display: 'block', margin: '5px auto',
              }} alt='No challenges yet' />
              <h4>No challenges are currently available</h4>
              {util.canCreateChallenges(user) && createNewAction
                && <Button primary onClick={createNewAction} content='Create a new challenge' icon='plus' />
              }
            </Segment>
          }
          {challenges?.length > 0
            && <>
              <div style={{ textAlign: 'right', marginBottom: 20 }}>
                <Input autoFocus icon='search' placeholder='Search for a challenge...' value={challengeSearchState.query} onChange={(e) => setChallengesSearchState((prev) => ({ ...prev, query: e.target.value }))} />
              </div>
              {challenges.length > 0
                ? (
                  <List verticalAlign='middle' size='large'>
                    {challenges.map((challenge) => (
                      <List.Item
                        key={challenge._id}
                        
                      >
                        <Image avatar src={challenge.bannerImageUrl} />
                        <List.Content>
                          <List.Header
                            as={Link}
                            to={`/challenges/${challenge._id}`}
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            {challenge.name}
                          </List.Header>
                        </List.Content>
                        <List.Content floated='right'>
                          <LargeCheckbox checked={selectedChallenge?._id === challenge._id} onChange={() => onClickChallenge(challenge)} />
                        </List.Content>
                      </List.Item>
                    ))}
                  </List>
                ) : (
                  <Segment placeholder textAlign='center'>
                    <h4>No challenges match your search term</h4>
                    <p>Adjust your query and try again.</p>
                  </Segment>
                )
              }
            </>
          }
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => (controlled ? onClose() : setOpen(false))} content='Cancel' />
          {util.canCreateChallenges(user) && challenges?.length > 0 && createNewAction
            && <ThemedButton primary onClick={createNewAction} content='Create a new challenge instead' icon='plus' />
          }
          {selectedChallenge
            ? (
              <ThemedButton
                primary
                onClick={() => onChoose(selectedChallenge)}
                content='Choose this challenge'
                icon='check'
                {...buttonProps}
              />
            ) : null
          }
        </Modal.Actions>
      </Modal>
    </>
  );
}

export default ChallengeChooser;
